"use client";

import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";

import { cn } from "../../lib/utils";

const InputOTP = React.forwardRef<
	React.ComponentRef<typeof OTPInput>,
	React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
	<OTPInput
		ref={ref}
		containerClassName={cn(
			"flex items-center gap-2 has-[:disabled]:opacity-50",
			containerClassName,
		)}
		className={cn("disabled:cursor-not-allowed", className)}
		{...props}
	/>
));
InputOTP.displayName = "InputOTP";

const InputOTPGroup = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("flex items-center gap-2", className)}
		{...props}
	/>
));
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<"div"> & { index: number }
>(({ index, className, ...props }, ref) => {
	const inputOTPContext = React.useContext(OTPInputContext);
	const slot = inputOTPContext.slots[index];

	if (!slot) {
		return null;
	}

	const { char, hasFakeCaret, isActive } = slot;

	return (
		<div
			ref={ref}
			data-slot="input-otp-slot"
			data-active={isActive || undefined}
			className={cn(
				"relative bg-background flex h-12 w-10 items-center justify-center rounded-md border border-neutral-200 text-sm font-bold transition-all",
				isActive &&
					"z-10 border-neutral-900 ring-1 ring-neutral-900 dark:border-neutral-300 dark:ring-neutral-300",
				"aria-[invalid=true]:border-danger-500 aria-[invalid=true]:ring-danger-500/20",
				className,
			)}
			{...props}
		>
			{char}
			{hasFakeCaret && (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
					<div className="h-5 w-px animate-caret-blink bg-neutral-900 duration-1000 dark:bg-neutral-50" />
				</div>
			)}
		</div>
	);
});
InputOTPSlot.displayName = "InputOTPSlot";

function InputOTPSeparator({ ...props }: React.ComponentProps<"div">) {
	return (
		<div role="separator" data-slot="input-otp-separator" {...props}>
			<MinusIcon />
		</div>
	);
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
